<template>
  <div v-if="mode==='order'">
    <draggable v-model="inputVal" draggable=".draggable-item" @change="onMoved">
      <div v-for="(datum, datumIndex) in inputVal" :key="datumIndex" class="draggable-item">
        {{ datum.text }}
        <i class="fa fa-arrows float-right" aria-hidden="true" />
      </div>
    </draggable>
    <div class="clear" />
    <button class="btn btn-light float-right set-mode" @click="cancelChanged()">
      {{ $t('common.button.cancel') }}
    </button>
    <button class="btn btn-light float-right set-mode" @click="setMode('normal')">
      {{ $t('common.button.save') }}
    </button>
    <div class="clear" />
  </div>
  <div v-else-if="orderable">
    <multiselect
      :id="id"
      v-model="inputVal"
      :options="optionsInternal"
      :multiple="multiple"
      label="text"
      :select-label="selectLabel"
      :select-group-label="selectGroupLabel"
      :deselect-label="deselectLabel"
      :deselect-group-label="deselectGroupLabel"
      :tag-placeholder="tagPlaceholder"
      :selected-label="selectedLabel"
      track-by="value"
      :disabled="readonly || disabled"
      :placeholder="placeholder"
      class="form-control"
      :name="name"
      @input="multiselectInput"
      @search-change="asyncSearch">
      <template slot="noResult">{{ $t('common.notification.no_data') }}</template>
      <template slot="noOptions">{{ $t('common.notification.no_data') }}</template>
    </multiselect>
    <div class="clear" />
    <button v-if="inputVal && inputVal.length > 1" class="btn btn-light float-right set-mode" @click="setMode('order')">
      {{ $t('common.button.change_ordr') }}
    </button>
    <div class="clear" />
  </div>
  <div v-else>
    <multiselect
      :id="id"
      v-model="inputVal"
      :options="optionsInternal"
      :multiple="multiple"
      label="text"
      :select-label="selectLabel"
      :select-group-label="selectGroupLabel"
      :deselect-label="deselectLabel"
      :deselect-group-label="deselectGroupLabel"
      :tag-placeholder="tagPlaceholder"
      :selected-label="selectedLabel"
      track-by="value"
      :disabled="readonly || disabled"
      :placeholder="placeholder"
      class="form-control"
      :name="name"
      @input="multiselectInput"
      @search-change="asyncSearch">
      <template slot="noResult">{{ $t('common.notification.no_data') }}</template>
      <template slot="noOptions">{{ $t('common.notification.no_data') }}</template>
    </multiselect>
  </div>
</template>


<script>
export default {
  props: {
    value: {
      type: [String, Number, Boolean, Array, Object],
      default: undefined
    },
    options: {
      type: Array,
      default: function() {
        return []
      }
    },
    readonly: {
      type: Boolean,
      default: false
    },
    visibleData: {
      type: Object,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    selectLabel: {
      type: String,
      default: ''
    },
    selectGroupLabel: {
      type: String,
      default: ''
    },
    deselectLabel: {
      type: String,
      default: ''
    },
    deselectGroupLabel: {
      type: String,
      default: ''
    },
    tagPlaceholder: {
      type: String,
      default: ''
    },
    selectedLabel: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    multiple: {
      type: Boolean,
      default: true
    },
    id: {
      type: String,
      default: ''
    },
    orderable: {
      type: Boolean,
      default: false
    },
    asyncSearchUrl: {
      type: String,
      default: undefined,
      required: false
    },
    asyncSearchFilter: {
      type: Function,
      default: undefined
    },
    asyncSearchMethod: {
      type: String,
      default: 'GET'
    }
  },
  data() {
    return {
      inputVal: '',
      mode: 'normal',
      optionsInternal: []
    }
  },
  watch: {
    value: function(newVal) {
      this.inputVal = newVal
    },
    options: {
      handler: function(newVal) {
        this.optionsInternal = this.options
      },
      deep: true
    }
  },
  mounted() {
    this.optionsInternal = this.options
    this.inputVal = this.value
  },
  methods: {
    setMode(mode) {
      if (mode === 'order') {
        this.originalVal = this.inputVal
      }
      this.mode = mode
    },
    multiselectInput(val) {
      this.$emit('input', val)
      this.$emit('change', val)
    },
    cancelChanged() {
      this.inputVal = this.originalVal
      this.$emit('input', this.inputVal)
      this.$emit('change', this.inputVal)
      this.setMode('normal')
    },
    onMoved() {
      this.$emit('input', this.inputVal)
      this.$emit('change', this.inputVal)
    },
    asyncSearch(query) {
      if (!this.asyncSearchUrl) {
        return
      }
      if (this.asyncSearchTimer) {
        clearTimeout(this.asyncSearchTimer)
        delete this.asyncSearchTimer
      }
      this.asyncSearchTimer = setTimeout(() => {
        if (this.asyncSearchTimer) {
          delete this.asyncSearchTimer
        }
        this.search(query)
      }, 500)
    },
    search(query) {
      let url = this.asyncSearchUrl

      if (this.asyncSearchUrl.endsWith('WithData')) {
        this.asyncSearchMethod = 'POST'
      }
      const successHandler = response => {
        if (this.asyncSearchFilter) {
          response = this.asyncSearchFilter(response)
        }
        if (response.resultCode === 'success') {
          this.optionsInternal = response.data
          this.$forceUpdate()
        }
      }
      const failHandler = error => {
        if (error.response && error.response.data) {
          console.error(this.$t(error.response.data.message))
          this.$toast.error(this.$t(error.response.data.message))
        } else {
          console.error(error)
          this.$toast.error(error)
        }
      }
      if (this.asyncSearchMethod === 'POST') {
        const data = {
          q: query,
          data: this.visibleData
        }
        this.$axios
          .$post(url, data)
          .then(successHandler)
          .catch(failHandler)
      } else {
        if (this.asyncSearchUrl.indexOf('?') >= 0) {
          url += '&'
        } else {
          url += '?'
        }
        url += 'q=' + query

        this.$axios
          .$get(url)
          .then(successHandler)
          .catch(failHandler)
      }
    }
  }
}
</script>

<style scoped>
.draggable-item {
  cursor: move;
  border: 1px solid #dddddd;
  border-radius: 5px;
  padding: 5px;
  margin-right: -2px;
}
.fa-arrows {
  margin: 4px;
}
button.set-mode {
  border-radius: 0 0 5px 5px;
  margin-right: -2px;
  margin-top: -2px;
  border: 2px solid #e4e7ea;
  padding: 0.175rem 0.5rem;
  font-size: 0.775rem;
}
>>> ::placeholder {
  color: #adadad;
}
>>> .multiselect {
  box-sizing: border-box;
  padding: 0;
}
>>> .multiselect__tags {
  border: none;
  background: transparent;
}
>>> .multiselect__placeholder {
  margin-left: 5px;
}
>>> input[type='text'] {
  margin-top: 3px;
  padding-bottom: 2px;
}
>>> .multiselect__input,
>>> .multiselect__single {
  background: transparent;
}
</style>
